import React, { useCallback, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { GetHighlightStyle } from "./utils/GetHighlightStyle";
import ArrowConnection from "./ArrowConnection";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// PDF.js worker setup
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewerWithHighlights = ({ pdfUrl, pages, onFieldClick, selectedKey, fieldRefs, inputRefs, showHighlights, setShowHighlights, noCoordinates }) => {
  const [pageSizes, setPageSizes] = useState({});
  const [zoom, setZoom] = useState(1.5); // default zoom level
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [arrowPosition, setArrowPosition] = useState(null);

  const pageRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const isProgrammaticScroll = useRef(false);

  const handleRenderSuccess = (pageNumber, width, height) => {
    setPageSizes((prev) => ({
      ...prev,
      [pageNumber]: { width, height },
    }));
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const scrollToPage = (pageNum) => {
    if (pageRefs.current[pageNum]) {
      isProgrammaticScroll.current = true;
      // pageRefs.current[pageNum].scrollIntoView({
      //   behavior: "smooth",
      //   block: "start",
      // });
      const element = pageRefs.current[pageNum];
      const container = scrollContainerRef.current;

      // Use a more direct approach similar to form submission
      const scrollPosition = element.offsetTop - container.offsetTop;
      container.scrollTop = scrollPosition;

      setCurrentPage(pageNum);
      // Give time for scroll to settle
      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 500); // adjust timing if needed
    }
  };

  const updateArrowPosition = useCallback(() => {
    const inputElement = selectedKey ? inputRefs.current[selectedKey] : null;
    const container = scrollContainerRef.current;

    if (!selectedKey || !inputElement || !container) {
      setArrowPosition(null);
      return;
    }

    const field = pages
      .flatMap((page) => page.fields)
      .find((field) => field.commonKeys.includes(selectedKey));

    if (!field) {
      setArrowPosition(null);
      return;
    }

    const highlightElement = fieldRefs.current[field.key];

    if (!highlightElement) {
      setArrowPosition(null);
      return;
    }

    // Force a reflow to ensure positions are up to date after smooth scrolls.
    void highlightElement.offsetHeight;

    const inputRect = inputElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const highlightRect = highlightElement.getBoundingClientRect();
    const displayResultContainer = inputElement.closest('.display-result-section') || document.querySelector('.display-result-section');
    const displayResultRect = displayResultContainer?.getBoundingClientRect();

    const isInputVisible = displayResultRect &&
      inputRect.top >= displayResultRect.top &&
      inputRect.bottom <= displayResultRect.bottom;

    const isHighlightVisible =
      highlightRect.top >= containerRect.top &&
      highlightRect.bottom <= containerRect.bottom;

    if (!isInputVisible || !isHighlightVisible) {
      setArrowPosition(null);
      return;
    }

    const inputIsRightOfHighlight = inputRect.left >= highlightRect.right;

    setArrowPosition({
      startX: inputIsRightOfHighlight ? inputRect.left : inputRect.right,
      startY: inputRect.top + (inputRect.height / 2),
      endX: inputIsRightOfHighlight ? highlightRect.right : highlightRect.left,
      endY: highlightRect.top + highlightRect.height / 2
    });
  }, [selectedKey, inputRefs, fieldRefs, pages]);

  useEffect(() => {
    // Update arrow position immediately when selectedKey changes
    updateArrowPosition();

    // Also update after a short delay to ensure DOM has settled
    const timeoutId = setTimeout(updateArrowPosition, 100);
    return () => clearTimeout(timeoutId);
  }, [selectedKey, updateArrowPosition]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      updateArrowPosition();
    };

    // Add scroll listener to the PDF container
    container.addEventListener("scroll", handleScroll);

    // Add scroll listener to the display result section
    const displayResultSection = selectedKey
      ? inputRefs.current[selectedKey]?.closest('.display-result-section') || document.querySelector('.display-result-section')
      : document.querySelector('.display-result-section');
    if (displayResultSection) {
      displayResultSection.addEventListener("scroll", handleScroll);
    }
    window.addEventListener("resize", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (displayResultSection) {
        displayResultSection.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("resize", handleScroll);
    };
  }, [selectedKey, inputRefs, updateArrowPosition]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const pageEntries = Object.entries(pageRefs.current);

      let visiblePage = null;
      let minDistance = Infinity;
      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + container.clientHeight / 2;

      for (const [pageNumStr, el] of pageEntries) {
        const rect = el.getBoundingClientRect();
        const elCenterY = rect.top + rect.height / 2;
        const distance = Math.abs(centerY - elCenterY);

        if (distance < minDistance) {
          minDistance = distance;
          visiblePage = Number(pageNumStr);
        }
      }

      if (!isProgrammaticScroll.current && visiblePage !== null && visiblePage !== currentPage) {
        setCurrentPage(visiblePage);
      }
    };

    const throttled = () => {
      if (!throttled.timeout) {
        throttled.timeout = setTimeout(() => {
          handleScroll();
          throttled.timeout = null;
        }, 100);
      }
    };

    container.addEventListener("scroll", throttled);
    return () => {
      container.removeEventListener("scroll", throttled);
      if (throttled.timeout) clearTimeout(throttled.timeout);
    };
  }, [currentPage]);

  return (
    <div className="relative w-full h-full">
      <div ref={scrollContainerRef} className="relative w-full h-full overflow-auto">
        {/* tool bar */}
        <div className="sticky top-4 left-0 z-50 w-fit mx-auto bg-white/95 backdrop-blur-sm shadow-lg rounded-full border border-gray-200 px-6 py-2 flex items-center justify-center gap-4 select-none">
          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const newPage = Math.max(1, currentPage - 1);
                if (newPage !== currentPage) {
                  scrollToPage(newPage);
                }
              }}
              disabled={currentPage <= 1}
              className={`p-2 rounded-full transition ${currentPage <= 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const page = parseInt(e.target.page.value, 10);
                if (page >= 1 && page <= numPages) {
                  scrollToPage(page);
                } else {
                  // Reset to current page if invalid
                  e.target.page.value = currentPage;
                }
              }}
              className="flex items-center gap-1"
            >
              <input
                name="page"
                type="number"
                min="1"
                max={numPages}
                defaultValue={currentPage}
                key={currentPage}
                className="w-12 px-2 py-1 text-sm border border-gray-200 rounded-full text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                onBlur={(e) => {
                  const page = parseInt(e.target.value, 10);
                  if (isNaN(page) || page < 1 || page > numPages) {
                    e.target.value = currentPage;
                  }
                }}
              />
              <span className="text-sm text-gray-600">of {numPages}</span>
            </form>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const newPage = Math.min(numPages, currentPage + 1);
                if (newPage !== currentPage) {
                  scrollToPage(newPage);
                }
              }}
              disabled={currentPage >= numPages}
              className={`p-2 rounded-full transition ${currentPage >= numPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-gray-200" />

          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.1))}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={() => setZoom(1.5)}
              disabled={zoom === 1.5}
              className={`text-sm font-medium transition ${zoom === 1.5
                ? "text-gray-700 cursor-default"
                : "text-indigo-600 hover:text-indigo-700"
                }`}
            >
              {(zoom * 100).toFixed(0)}%
            </button>
            <button
              onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Divider and Highlights toggle */}
          {!noCoordinates && (
            <>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showHighlights}
                    onChange={() => setShowHighlights((prev) => !prev)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Show highlights</span>
                </label>
              </div>
            </>
          )}
        </div>

        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          className="flex flex-col items-center"
        >
          {pages.map(({ pageNumber, fields, originalWidth, originalHeight }) => {
            const pageSize = pageSizes[pageNumber] || { width: 1, height: 1 };
            const scaleX = pageSize.width / originalWidth;
            const scaleY = pageSize.height / originalHeight;

            return (
              <div
                key={pageNumber}
                ref={(el) => {
                  if (el) pageRefs.current[pageNumber] = el;
                }}
                className="relative mb-5 pointer-events-none"
              >
                <Page
                  scale={zoom}
                  pageNumber={pageNumber}
                  onRenderSuccess={({ width, height }) =>
                    handleRenderSuccess(pageNumber, width, height)
                  }
                />
                {/* Highlight overlay */}
                <div
                  className="absolute top-0 left-0"
                  style={{
                    width: pageSize.width,
                    height: pageSize.height,
                  }}
                >
                  {showHighlights && fields.map((field, i) => {
                    const style = GetHighlightStyle(field, scaleX, scaleY);
                    const isSelected = field.commonKeys.includes(selectedKey);

                    return (
                      <div
                        key={i}
                        ref={(el) => {
                          if (el) fieldRefs.current[field.key] = el;
                        }}
                        className="absolute group cursor-pointer pointer-events-auto"
                        style={{
                          left: style.left - 5,
                          top: style.top - 5,
                          width: style.width + 10,
                          height: style.height + 10,
                        }}
                        onClick={() => {
                          onFieldClick?.(field.key);
                        }}
                      >
                        {/* Highlight Box */}
                        <div
                          className={`w-full h-full rounded-sm bg-yellow-200 bg-opacity-30 border-2 transition-colors duration-150 ${isSelected ? "border-indigo-500" : "border-yellow-400"
                            }`}
                        />

                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 dureation-800 shadow-lg">
                          {field.commonKeys.join(', ')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </Document>
      </div>

      {/* Arrow connection - rendered at root level with high z-index */}
      {arrowPosition && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 pointer-events-none z-[100]">
          <ArrowConnection
            startX={arrowPosition.startX}
            startY={arrowPosition.startY}
            endX={arrowPosition.endX}
            endY={arrowPosition.endY}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default PDFViewerWithHighlights;
